export const TRAINING_TYPES_UPDATED_EVENT = 'training_types:updated';

export const SESSION_SAFE_UPDATE = 'SESSION_SAFE_UPDATE';
export const SESSION_INVALIDATING = 'SESSION_INVALIDATING';

export const IMPLICIT_UPDATE_AUDIT_FIELDS = Object.freeze([
  'updated_at',
  'updated_by_user',
  'updated_by_user_id',
]);

const MEMBERSHIP_FIELDS = new Set(['status', 'validity', 'id']);
const ALWAYS_INVALIDATING_FIELDS = new Set(['change_before', 'files']);

const FIELD_ALIASES = {
  name: ['name'],
  department_id: ['department_id', 'department.name'],
  'department.name': ['department_id', 'department.name'],
  type: ['type'],
  interval: ['interval'],
  training_company_id: ['training_company_id', 'training_company.name'],
  'training_company.name': ['training_company_id', 'training_company.name'],
  machines: ['machines', 'machine.name'],
  'machine.name': ['machines', 'machine.name'],
  employee_id: ['employee_id', 'employee.name'],
  'employee.name': ['employee_id', 'employee.name'],
  description: ['description'],
  status: ['status', 'validity'],
  validity: ['status', 'validity'],
  id: ['id'],
  updated_at: ['updated_at'],
  updated_by_user: ['updated_at'],
  updated_by_user_id: ['updated_at'],
  created_at: ['created_at'],
  created_by_user: ['created_at'],
  created_by_user_id: ['created_at'],
};

function unwrapTrainingTypeRecord(payload) {
  if (payload == null || typeof payload !== 'object') return null;
  if (payload.data && typeof payload.data === 'object' && !Array.isArray(payload.data) && payload.data.id != null) {
    return payload.data;
  }
  return payload;
}

function scalar(value) {
  if (value == null) return '';
  if (typeof value === 'object') {
    if (value.id != null) return String(value.id);
    return '';
  }
  return String(value);
}

function machineIdsKey(machines) {
  const ids = (Array.isArray(machines) ? machines : [])
    .map((machine) => (machine && typeof machine === 'object' ? machine.id : machine))
    .filter((id) => id != null && id !== '')
    .map((id) => String(id))
    .sort();
  return ids.join(',');
}

function employeeIdOf(value) {
  if (value && typeof value === 'object') return value.id ?? '';
  return value ?? '';
}

function expandFields(fields) {
  const expanded = new Set();
  (Array.isArray(fields) ? fields : []).forEach((field) => {
    if (field == null || field === '') return;
    const aliases = FIELD_ALIASES[field] || [field];
    aliases.forEach((alias) => expanded.add(alias));
  });
  return expanded;
}

function hasActiveValue(value) {
  if (value == null) return false;
  if (Array.isArray(value)) return value.some((item) => hasActiveValue(item));
  return String(value).trim() !== '';
}

function isDefaultIdAscSorter(sorter) {
  const field = sorter?.field || sorter?.column;
  const dir = sorter?.dir || 'asc';
  return field === 'id' && dir === 'asc';
}

export function isTableCompleteTrainingTypeRow(row) {
  if (!row || typeof row !== 'object' || Array.isArray(row)) return false;
  if (row.id == null || row.id === '') return false;
  return Array.isArray(row.machines);
}

export function collectTrainingTypeChangedFields(previous, next, options = {}) {
  const prev = previous && typeof previous === 'object' ? previous : {};
  const nxt = next && typeof next === 'object' ? next : {};
  const changed = [];
  ['name', 'department_id', 'type', 'interval', 'training_company_id', 'description'].forEach((key) => {
    if (scalar(prev[key]) !== scalar(nxt[key])) changed.push(key);
  });
  const prevStatus = prev.status == null || prev.status === '' ? 'Aktívny' : String(prev.status);
  const nextStatus = nxt.status == null || nxt.status === '' ? 'Aktívny' : String(nxt.status);
  if (prevStatus !== nextStatus) changed.push('status');
  if (scalar(employeeIdOf(prev.employee_id ?? prev.employee)) !== scalar(employeeIdOf(nxt.employee_id ?? nxt.employee))) {
    changed.push('employee_id');
  }
  if (machineIdsKey(prev.machines) !== machineIdsKey(nxt.machines)) changed.push('machines');
  if (prev.id != null && nxt.id != null && String(prev.id) !== String(nxt.id)) changed.push('id');
  if (options.implicitAudit) changed.push(...IMPLICIT_UPDATE_AUDIT_FIELDS);
  if (options.changeBefore) changed.push('change_before');
  if (options.hasFiles) changed.push('files');
  return Array.from(new Set(changed));
}

export function classifyTrainingTypeListMutation(event, session = {}) {
  const detail = event?.detail;
  if (!detail || typeof detail !== 'object') return SESSION_INVALIDATING;
  if (detail.operation !== 'update') return SESSION_INVALIDATING;
  if (!Array.isArray(detail.changedFields) || detail.changedFields.length === 0) return SESSION_INVALIDATING;
  const row = unwrapTrainingTypeRecord(detail.row);
  if (!isTableCompleteTrainingTypeRow(row)) return SESSION_INVALIDATING;
  const id = detail.id ?? row.id;
  if (id == null || id === '') return SESSION_INVALIDATING;
  if (typeof session.isRowLoaded !== 'function' || !session.isRowLoaded(id)) return SESSION_INVALIDATING;
  if (String(session.toolbarSearch ?? '').trim() !== '') return SESSION_INVALIDATING;

  const changedFields = Array.from(new Set([
    ...detail.changedFields,
    ...IMPLICIT_UPDATE_AUDIT_FIELDS,
  ]));
  const expanded = expandFields(changedFields);
  for (const field of expanded) {
    if (MEMBERSHIP_FIELDS.has(field) || ALWAYS_INVALIDATING_FIELDS.has(field)) return SESSION_INVALIDATING;
  }
  if (changedFields.some((field) => ALWAYS_INVALIDATING_FIELDS.has(field))) return SESSION_INVALIDATING;

  const filters = Array.isArray(session.activeHeaderFilters) ? session.activeHeaderFilters : [];
  for (const filter of filters) {
    if (!filter?.field || !hasActiveValue(filter.value)) continue;
    const aliases = expandFields([filter.field]);
    for (const alias of aliases) {
      if (expanded.has(alias)) return SESSION_INVALIDATING;
    }
  }

  const sorters = Array.isArray(session.activeSorters) ? session.activeSorters : [];
  for (const sorter of sorters) {
    const field = sorter?.field || sorter?.column;
    if (!field) continue;
    if (isDefaultIdAscSorter(sorter) && !expanded.has('id')) continue;
    const aliases = expandFields([field]);
    for (const alias of aliases) {
      if (expanded.has(alias)) return SESSION_INVALIDATING;
    }
  }

  return SESSION_SAFE_UPDATE;
}

export function applyPagedMutationOverlay(response, overlay) {
  if (!response || typeof response !== 'object' || !Array.isArray(response.data)) return response;
  if (!overlay || typeof overlay.get !== 'function' || overlay.size === 0) return response;
  return {
    ...response,
    data: response.data.map((row) => {
      if (!row || row.id == null) return row;
      return overlay.get(String(row.id)) || row;
    }),
  };
}

export function emitTrainingTypeListMutation(detail) {
  window.dispatchEvent(new CustomEvent(TRAINING_TYPES_UPDATED_EVENT, {
    detail: detail && typeof detail === 'object' ? detail : { operation: 'invalidate' },
  }));
}

export function emitTrainingTypeListRefresh(detail) {
  emitTrainingTypeListMutation(detail);
}
